interface UsageIdentityTracker {
	identify?(token: string): Promise<void>
	resetIdentity?(): Promise<void>
}
type TrackerWindow = Window & { CGAgentGuard?: UsageIdentityTracker }
const SCRIPT_ID = 'cg-production-usage-tracker'

/** Optional, asynchronous capture. A failed collector cannot block the application. */
export function loadUsageTracker(win: Window): void {
	if (win.location.origin !== 'https://app.clickguard.com') return
	try {
		if (win.document.getElementById(SCRIPT_ID)) return
		const script = win.document.createElement('script')
		script.id = SCRIPT_ID
		script.src = 'https://api2.clickguard.com/__usage/tracker.js'
		script.async = true
		script.crossOrigin = 'anonymous'
		script.referrerPolicy = 'no-referrer'
		script.onerror = () => script.remove()
		win.document.head.appendChild(script)
	} catch {
		// Collection is fail-open; application access remains independent.
	}
}

const currentActors = new WeakMap<Window, string | undefined>()

/** Explicit Auth0 handoff. No token is stored, broadcast, logged, or sent on unrelated requests. */
export function attachUsageIdentity(
	win: TrackerWindow,
	authenticated: boolean,
	getAccessToken: () => Promise<string>,
	actor?: string
): () => void {
	if (win.location.origin !== 'https://app.clickguard.com') return () => {}
	let stopped = false
	let busy = false
	let reset = false
	// React's verified Auth0 lifecycle is only a rotation hint, not identity proof.
	// A changed subject or logout closes the previous accumulator synchronously;
	// the collector still cryptographically verifies every subsequent binding.
	const changed =
		currentActors.has(win) &&
		currentActors.get(win) !== (authenticated ? actor : undefined)
	currentActors.set(win, authenticated ? actor : undefined)
	const rotation =
		changed && win.CGAgentGuard?.resetIdentity
			? win.CGAgentGuard.resetIdentity().catch(() => {})
			: Promise.resolve()
	if (changed && !authenticated) reset = true
	const synchronize = async () => {
		if (stopped || busy) return
		if (authenticated) loadUsageTracker(win)
		const tracker = win.CGAgentGuard
		if (!tracker) return
		busy = true
		try {
			await rotation
			if (stopped || tracker !== win.CGAgentGuard) return
			if (!authenticated) {
				if (!reset && tracker.resetIdentity) {
					reset = true
					await tracker.resetIdentity()
				}
				return
			}
			if (!tracker.identify) return
			const token = await getAccessToken()
			if (stopped || tracker !== win.CGAgentGuard) return
			await tracker.identify(token)
		} catch {
			// Observability must never redirect, log out, or block the platform.
		} finally {
			busy = false
		}
	}
	const ready = () => {
		void synchronize()
	}
	const visible = () => {
		if (win.document.visibilityState === 'visible') ready()
	}
	win.addEventListener('cg-usage-tracker-ready', ready)
	win.document.addEventListener('visibilitychange', visible)
	// Bounded retry also refreshes expiring Auth0 proofs without persisting credentials.
	const timer = win.setInterval(ready, 60_000)
	ready()
	return () => {
		stopped = true
		win.clearInterval(timer)
		win.removeEventListener('cg-usage-tracker-ready', ready)
		win.document.removeEventListener('visibilitychange', visible)
	}
}
